import './styles.scss';

import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import AltSurfaceBuilder from '../../components/alt-surface-builder';
import CustomTab from '../../components/custom-tab';
import Gallery from '../../components/gallery';
import Overview from '../../components/overview';
import Tabs from '../../components/tabs';
import MarketChange from '../market-change';
import { CurrencyTypeEnum } from '../../model/currency_type.enum';
import { DashboardTabsKeysEnum } from '../../model/dashboard-tabs';
import AuthContext from '../../providers/auth/auth-service';
import { DashboardContext } from '../../providers/dashboard-provider';
import { GalleryProvider } from '../../providers/gallery-provider';
import { GlobalContext } from '../../providers/global-provider';
import { OverviewProvider } from '../../providers/overview-provider';

export default function Dashboard() {
  const { marketDataOverviewCurrentTab } = useContext(GlobalContext);
  const { tabs } = useContext(DashboardContext);
  const { permissions, iLogout, ensureImmersiveSession } = useContext(AuthContext);
  const { logout } = useAuth0();

  useEffect(() => {
    document.title = 'Immersive Finance - Dashboard';

    // IM-1991 fix #4: empty permissions on mount (e.g. a refresh that raced the
    // JWT re-exchange) must NOT bounce straight to Auth0. Attempt the guarded
    // re-exchange first; only log out if that genuinely fails (Auth0 session
    // gone / backend reject).
    if (permissions.length > 0) {
      return;
    }

    let cancelled = false;
    ensureImmersiveSession().then((outcome) => {
      if (cancelled || outcome === 'ok') {
        return;
      }
      iLogout();
      logout({ logoutParams: { returnTo: import.meta.env.REACT_APP_AUTH0_LOGOUT } });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const renderTabContent = useCallback((tabKey: string) => {
    if (tabKey.startsWith('custom_tab_')) {
      return <CustomTab tabKey={tabKey} />;
    }

    if (tabKey === DashboardTabsKeysEnum.volLab) {
      return (
        <GalleryProvider>
          <Gallery />
        </GalleryProvider>
      );
    }

    if (tabKey === DashboardTabsKeysEnum.altSurfaceBuilder) {
      return <AltSurfaceBuilder />;
    }

    if (tabKey === DashboardTabsKeysEnum.marketChange) {
      return <MarketChange />;
    }

    const currency = tabKey.includes('overview_')
      ? tabKey.replace('overview_', '').toUpperCase()
      : CurrencyTypeEnum.BITCOIN;
    return (
      <OverviewProvider currency={currency as CurrencyTypeEnum}>
        <Overview currency={currency as CurrencyTypeEnum} />
      </OverviewProvider>
    );
  }, []);

  const renderTabs = () => {
    return tabs.map((tab) => {
      const isCurrentTab = marketDataOverviewCurrentTab === tab.key;
      return (
        <div key={tab.key} className={`tab-wrapper ${isCurrentTab ? 'visible' : ''}`}>
          {renderTabContent(tab.key)}
        </div>
      );
    });
  };

  const renderDashboard = useMemo(() => {
    return (
      <div className="dashboard-container">
        <Tabs />
        {renderTabs()}
      </div>
    );
  }, [marketDataOverviewCurrentTab]);

  return renderDashboard;
}
